import { z } from 'zod';

// The CMP disbursement report of a batch (JSON twin of the batch Excel), shared
// by the MIS routing batches and the Accounts batches.

const cmpTotalsSchema = z.object({
  salesTarget: z.number(),
  cmpAmount: z.number(),
  cmpPercent: z.number(),
  instituteCount: z.number(),
});

export type PcmpCmpTotals = z.infer<typeof cmpTotalsSchema>;

/** Columns a report adds after the payment: the item's status, an urgent item's MIS batch. */
export const PCMP_CMP_REPORT_EXTRA_COLUMNS = ['STATUS', 'ORIG_BATCH'] as const;
export type PcmpCmpReportExtraColumn = (typeof PCMP_CMP_REPORT_EXTRA_COLUMNS)[number];

const cmpReportRowSchema = z.object({
  sl: z.number(),
  proposalId: z.coerce.number(),
  refNo: z.string(),
  division: z.string(),
  region: z.string().nullable(),
  projectName: z.string(),
  moInfo: z.string(),
  moName: z.string().nullable(),
  moHrEmpId: z.string().nullable(),
  moMobile: z.string().nullable(),
  instituteId: z.coerce.number().nullable(),
  instituteName: z.string().nullable(),
  instituteThana: z.string().nullable(),
  instituteDistrict: z.string().nullable(),
  teacherName: z.string(),
  studentQty: z.number(),
  salesTarget: z.number(),
  grammarPercent: z.number().nullable(),
  practicePercent: z.number().nullable(),
  cmpPercent: z.number(),
  perStudentCost: z.number(),
  cmpAmount: z.number(),
  proposedAmount: z.number(),
  payment: z.object({
    type: z.string().nullable(),
    holderName: z.string().nullable(),
    accountNo: z.string().nullable(),
    bankName: z.string().nullable(),
    branchName: z.string().nullable(),
    routingNo: z.string().nullable(),
    mobileNo: z.string().nullable(),
    onBehalf: z.string().nullable(),
  }),
  promotionType: z.string().nullable(),
  status: z.string().nullish(),
  origBatchNo: z.number().nullish(),
});

export type PcmpCmpReportRow = z.infer<typeof cmpReportRowSchema>;

const cmpSectionShape = {
  proposalQty: z.number(),
  proposedAmount: z.number(),
  current: cmpTotalsSchema,
  overall: cmpTotalsSchema,
};

export const pcmpCmpReportSchema = z.object({
  title: z.string(),
  company: z.object({ name: z.string(), address: z.string() }),
  reportDate: z.string(),
  /** The detail table's headings, as the Excel has them (extra columns last). */
  columns: z.array(z.string()),
  extraColumns: z.array(z.enum(PCMP_CMP_REPORT_EXTRA_COLUMNS)).catch([]),
  ...cmpSectionShape,
  sections: z.array(
    z.object({ division: z.string(), ...cmpSectionShape, rows: z.array(cmpReportRowSchema) }),
  ),
});

export type PcmpCmpReport = z.infer<typeof pcmpCmpReportSchema>;
export type PcmpCmpReportSection = PcmpCmpReport['sections'][number];
